import { MetricEnum } from "reports/utils/metricTypes";
import { PnlCategoryLabelsEnum } from "../../../types";
import { MetricExplainerInputType, EquationType } from "./metricExplainerTypes";

export type MetricObjectType = {
  name: string;
  nameTranslated: string;
  longname?: string;
  numerator: EquationType;
  denominator: EquationType;
};

const TEEPExplainer = {
  name: MetricEnum.TEEP,
  longname: "TEEP (Total Effective Equipment Performance)",
  numerator: [{ name: PnlCategoryLabelsEnum.producing }],
  denominator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
    { name: PnlCategoryLabelsEnum.notScheduled },
  ],
};

const uptimeExplainer = {
  name: MetricEnum.UPTIME,
  numerator: [{ name: PnlCategoryLabelsEnum.producing }],
  denominator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
  ],
};

const lineEfficiencyExplainer = {
  name: "Line Efficiency",
  numerator: [{ name: "Net Output" }],
  denominator: [
    { name: "Target Rate", symbol: "*" },
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
  ],
};

const engDowntimeExplainer = {
  name: "Engineering Downtime",
  numerator: [{ name: PnlCategoryLabelsEnum.unplanned }],
  denominator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
  ],
};

const availabilityExplainer = {
  name: MetricEnum.AVAIL,
  numerator: [{ name: PnlCategoryLabelsEnum.producing }],
  denominator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
  ],
};

const taggingPercentExplainer = {
  name: MetricEnum.TAGGING,
  numerator: [
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
  ],
  denominator: [
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
  ],
};

const SEPExplainer = {
  name: "SEP",
  numerator: [{ name: "Net Output" }],
  denominator: [{ name: "Standard Rate * Scheduled Time" }],
};
const TEPExplainer = {
  name: "TEP",
  numerator: [{ name: "Net Output" }],
  denominator: [{ name: "Standard Rate * All Time" }],
};
const EfficiencyExplainer = {
  name: "Efficiency",
  numerator: [{ name: "Net Output" }],
  denominator: [
    { name: "Standard Rate * (Scheduled Production Time + Untagged Time)" },
  ],
};
const UtilizationExplainer = {
  name: "Utilization",
  numerator: [{ name: PnlCategoryLabelsEnum.producing }],
  denominator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
    { name: PnlCategoryLabelsEnum.notScheduled },
  ],
};
const RuntimeExplainer = {
  name: "Runtime",
  numerator: [{ name: PnlCategoryLabelsEnum.producing }],
  denominator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
  ],
};
const COTExplainer = {
  name: "COT",
  numerator: [{ name: "Changeover Time" }],
  denominator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
    { name: PnlCategoryLabelsEnum.notScheduled },
  ],
};
const UTTExplainer = {
  name: "UTT",
  numerator: [{ name: PnlCategoryLabelsEnum.unknown }],
  denominator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
    { name: PnlCategoryLabelsEnum.notScheduled },
  ],
};
const NSTExplainer = {
  name: "NST",
  numerator: [{ name: PnlCategoryLabelsEnum.notScheduled }],
  denominator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
    { name: PnlCategoryLabelsEnum.notScheduled },
  ],
};
const IBPExplainer = {
  name: "IBP",
  numerator: [
    { name: "Engineering Study" },
    { name: "Validation/Qualification" },
    { name: "Equipment PM" },
    { name: "Facilities work" },
  ],
  denominator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
    { name: PnlCategoryLabelsEnum.notScheduled },
  ],
};
const SPTExplainer = {
  name: "SPT",
  numerator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
  ],
  denominator: [
    { name: PnlCategoryLabelsEnum.producing },
    { name: PnlCategoryLabelsEnum.planned },
    { name: PnlCategoryLabelsEnum.unplanned },
    { name: PnlCategoryLabelsEnum.waiting },
    { name: PnlCategoryLabelsEnum.unknown },
    { name: PnlCategoryLabelsEnum.notScheduled },
  ],
};

const METRICS_LIST = [
  TEEPExplainer,
  uptimeExplainer,
  lineEfficiencyExplainer,
  engDowntimeExplainer,
  availabilityExplainer,
  taggingPercentExplainer,
  SEPExplainer,
  TEPExplainer,
  EfficiencyExplainer,
  UtilizationExplainer,
  RuntimeExplainer,
  COTExplainer,
  UTTExplainer,
  NSTExplainer,
  IBPExplainer,
  SPTExplainer,
];

function getMetricExplainer({
  name,
  nameTranslated,
  title,
  description,
}: MetricExplainerInputType) {
  const metricTitle = name.toLowerCase();
  const nameTranslatedLower = nameTranslated.toLowerCase();

  const explainer = METRICS_LIST.find(({ name: metricListName }) =>
    [metricTitle, nameTranslatedLower].includes(metricListName.toLowerCase())
  );

  // Override default name and longname if title is provided
  if (title && explainer) {
    return { ...explainer, name: title, longname: title, nameTranslated };
  }
  return explainer
    ? { ...explainer, nameTranslated }
    : { description, name, nameTranslated };
}

export const getMetricExplainers = (
  metricExplainerArr: MetricExplainerInputType[]
) => {
  return metricExplainerArr
    .map(metricExplainer => {
      return getMetricExplainer(metricExplainer);
    })
    .filter(metric => metric !== undefined) as (
    | MetricObjectType
    | { description: string; name: string; nameTranslated: string }
  )[];
};
